import React, { useCallback, useEffect, useRef, useState } from 'react';
import './Header.css';
import './AuthModal.css';
import KeepMyAssetLogo from './KeepMyAssetLogo';
import Modal from 'react-modal';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LuBookOpen, LuCheck, LuCircleHelp, LuHouse, LuInfo, LuLayoutGrid, LuMenu, LuMessageCircle, LuMessageSquare, LuRoute, LuShieldCheck, LuX } from 'react-icons/lu';

const GOOGLE_MODAL_BUTTON_ID = 'google-sign-in-modal-container';

const Header = ({
  user,
  onLogout,
  onGoogleResponse,
  googleClientId,
  onNavigateHome,
  onNavigateNomineeCheck,
  onNavigateNomineeLookup,
  onNavigateVerificationHistory,
  onNavigateAdmin,
  isAdmin,
  currentPage,
  showDashboardSidebar = false,
  isSidebarDrawerOpen = false,
  onToggleSidebarDrawer,
  onCloseSidebarDrawer,
}) => {
  const isGoogleInitialized = useRef(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isGoogleButtonRendered, setIsGoogleButtonRendered] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const isHomeActive = location.pathname === '/' && !location.hash;
  const isFeaturesActive = location.pathname === '/' && location.hash === '#features';

  const closeMobileMenu = () => setIsMobileMenuOpen(false);
  const navLinkClass = (active) => (active ? 'is-active' : undefined);

  const scrollToSection = (sectionId) => {
    navigate({ pathname: '/', hash: `#${sectionId}` }, { replace: true });
    window.setTimeout(() => {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  };

  const handleHomeNav = (event) => {
    closeMobileMenu();
    if (location.pathname !== '/') return;

    event.preventDefault();
    if (location.hash) {
      navigate('/', { replace: true });
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  };

  const handleFeaturesNav = (event) => {
    event.preventDefault();
    closeMobileMenu();
    if (location.pathname === '/') {
      scrollToSection('features');
      return;
    }
    navigate({ pathname: '/', hash: '#features' });
  };

  const isNavActive = (path) => {
    if (path === '/blog') {
      return location.pathname === '/blog' || location.pathname.startsWith('/blog/');
    }
    return location.pathname === path;
  };

  const handleGoogleResponse = useCallback((response) => {
    setIsAuthModalOpen(false);
    onGoogleResponse(response);
  }, [onGoogleResponse]);

  useEffect(() => {
    const initializeGoogleButton = () => {
      if (!window.google || !googleClientId || isGoogleInitialized.current) return false;
      try {
        window.google.accounts.id.initialize({
          client_id: googleClientId,
          callback: handleGoogleResponse,
        });
        isGoogleInitialized.current = true;
        return true;
      } catch (err) {
        console.error('Failed to initialize Google Sign-In:', err);
      }
      return false;
    };

    if (initializeGoogleButton()) return;

    const script = document.querySelector('script[src^="https://accounts.google.com/gsi/client"]');
    const onScriptLoad = () => {
      initializeGoogleButton();
    };
    if (script) {
      script.addEventListener('load', onScriptLoad);
    }

    const startTime = Date.now();
    const pollIntervalId = window.setInterval(() => {
      if (isGoogleInitialized.current) {
        window.clearInterval(pollIntervalId);
        return;
      }
      if (Date.now() - startTime > 7000) {
        window.clearInterval(pollIntervalId);
        return;
      }
      initializeGoogleButton();
    }, 200);

    return () => {
      if (script) script.removeEventListener('load', onScriptLoad);
      window.clearInterval(pollIntervalId);
    };
  }, [googleClientId, handleGoogleResponse]);

  useEffect(() => {
    try {
      const root = document.getElementById('root');
      if (root) Modal.setAppElement(root);
    } catch (_) {}
  }, []);

  useEffect(() => {
    const openModalListener = () => setIsAuthModalOpen(true);
    window.addEventListener('open-auth-modal', openModalListener);
    return () => window.removeEventListener('open-auth-modal', openModalListener);
  }, []);

  useEffect(() => {
    if (!isAuthModalOpen) return;
    const renderGoogleBtn = () => {
      try {
        if (!window.google?.accounts?.id) return false;
        const container = document.getElementById(GOOGLE_MODAL_BUTTON_ID);
        if (!container) return false;
        container.innerHTML = '';
        window.google.accounts.id.renderButton(container, {
          theme: 'outline',
          size: 'large',
          width: 280,
          text: 'signin_with',
          shape: 'pill',
        });
        setIsGoogleButtonRendered(true);
        return true;
      } catch (err) {
        console.error('Failed to render Google Sign-In button:', err);
        return false;
      }
    };
    setIsGoogleButtonRendered(false);
    let attempts = 0;
    const intervalId = window.setInterval(() => {
      attempts += 1;
      if (renderGoogleBtn() || attempts >= 12) {
        window.clearInterval(intervalId);
      }
    }, 250);
    return () => window.clearInterval(intervalId);
  }, [isAuthModalOpen, googleClientId]);

  useEffect(() => {
    if (user && isAuthModalOpen) {
      setIsAuthModalOpen(false);
    }
  }, [user, isAuthModalOpen]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === 'Escape') setIsMobileMenuOpen(false);
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    document.body.classList.toggle('mobile-nav-open', isMobileMenuOpen);
    return () => document.body.classList.remove('mobile-nav-open');
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (!isProfileMenuOpen) return undefined;

    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') setIsProfileMenuOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isProfileMenuOpen]);

  const useDashboardDrawer = Boolean(user && showDashboardSidebar);

  const handleMenuToggle = () => {
    if (useDashboardDrawer && onToggleSidebarDrawer) {
      onToggleSidebarDrawer();
      setIsMobileMenuOpen(false);
      return;
    }
    setIsMobileMenuOpen((open) => !open);
  };

  const menuToggleOpen = useDashboardDrawer ? isSidebarDrawerOpen : isMobileMenuOpen;

  const renderGoogleFallback = () => {
    try {
      if (window.google?.accounts?.id) {
        const c = document.getElementById(GOOGLE_MODAL_BUTTON_ID);
        if (c) {
          c.innerHTML = '';
          window.google.accounts.id.renderButton(c, {
            theme: 'outline',
            size: 'large',
            width: 280,
            text: 'signin_with',
            shape: 'pill',
          });
          setIsGoogleButtonRendered(true);
        }
      }
    } catch (_) {}
  };

  return (
    <>
      <header className={`header ${user ? 'header--authenticated' : ''} ${useDashboardDrawer ? 'header--dashboard' : ''}`}>
        <button className="brand-button" type="button" onClick={onNavigateHome} aria-label="KeepMyAsset home">
          <KeepMyAssetLogo />
        </button>

        {!user && (
          <nav className="public-nav public-nav-desktop" aria-label="Main navigation">
            <Link to="/" className={navLinkClass(isHomeActive)} onClick={handleHomeNav}>
              <LuHouse aria-hidden="true" /> Home
            </Link>
            <Link
              to="/#features"
              className={navLinkClass(isFeaturesActive)}
              onClick={handleFeaturesNav}
            >
              Features
            </Link>
            <Link to="/how-it-works" className={navLinkClass(isNavActive('/how-it-works'))}>
              How it works
            </Link>
            <Link to="/security" className={navLinkClass(isNavActive('/security'))}>
              Security
            </Link>
            <Link to="/faq" className={navLinkClass(isNavActive('/faq'))}>
              FAQ
            </Link>
            <Link to="/about" className={navLinkClass(isNavActive('/about'))}>
              About
            </Link>
            <Link to="/blog" className={navLinkClass(isNavActive('/blog'))}>
              Blog
            </Link>
            <Link to="/feedback" className={navLinkClass(isNavActive('/feedback'))}>
              Feedback
            </Link>
            <a href="https://wa.me/918318959079" target="_blank" rel="noopener noreferrer">Contact</a>
          </nav>
        )}

        <div className="auth-section auth-section-desktop">
          {!user && (
            <>
              <button type="button" className="header-signin" onClick={() => setIsAuthModalOpen(true)}>
                Login
              </button>
              <button type="button" className="header-cta" onClick={() => setIsAuthModalOpen(true)}>
                Secure Assets
              </button>
            </>
          )}

          {user && (
            <div className="user-info-container user-info-container--desktop">
              {user.picture && (
                <img src={user.picture} alt="" className="user-avatar" />
              )}
              <div className="user-details">
                <div className="user-name">{user.name}</div>
                <div className="user-email">{user.email}</div>
              </div>
              <button type="button" onClick={onLogout} className="logout-link">
                Logout
              </button>
            </div>
          )}
        </div>

        {user && !useDashboardDrawer ? (
          <div className="header-mobile-profile" ref={profileMenuRef}>
            <button
              type="button"
              className="header-avatar-btn"
              aria-label="Open account menu"
              aria-expanded={isProfileMenuOpen}
              aria-haspopup="menu"
              onClick={() => setIsProfileMenuOpen((open) => !open)}
            >
              {user.picture ? (
                <img src={user.picture} alt="" className="header-avatar-img" />
              ) : (
                <span className="header-avatar-fallback" aria-hidden="true">
                  {(user.name || 'U').charAt(0).toUpperCase()}
                </span>
              )}
            </button>
            {isProfileMenuOpen ? (
              <div className="header-profile-menu" role="menu">
                <div className="header-profile-menu-user">
                  <strong>{user.name}</strong>
                  <span>{user.email}</span>
                </div>
                <button
                  type="button"
                  className="header-profile-menu-item header-profile-menu-item--danger"
                  role="menuitem"
                  onClick={() => {
                    setIsProfileMenuOpen(false);
                    onLogout();
                  }}
                >
                  Logout
                </button>
              </div>
            ) : null}
          </div>
        ) : null}

        {!useDashboardDrawer ? (
          <button
            className={`menu-toggle ${menuToggleOpen ? 'is-open' : ''}`}
            type="button"
            aria-label={menuToggleOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuToggleOpen}
            aria-controls={!user ? 'public-mobile-drawer' : 'mobile-navigation'}
            onClick={handleMenuToggle}
          >
            {menuToggleOpen ? <LuX aria-hidden="true" size={32} strokeWidth={2.75} /> : <LuMenu aria-hidden="true" size={32} strokeWidth={2.75} />}
          </button>
        ) : null}

        {useDashboardDrawer ? (
          <button
            className={`sidebar-drawer-toggle ${menuToggleOpen ? 'is-open' : ''}`}
            type="button"
            aria-label={menuToggleOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={menuToggleOpen}
            onClick={handleMenuToggle}
          >
            {menuToggleOpen ? <LuX aria-hidden="true" size={32} strokeWidth={2.75} /> : <LuMenu aria-hidden="true" size={32} strokeWidth={2.75} />}
          </button>
        ) : null}

        <Modal
          isOpen={isAuthModalOpen}
          onRequestClose={() => setIsAuthModalOpen(false)}
          contentLabel="Sign in"
          className="auth-modal"
          overlayClassName="auth-modal-overlay"
          style={{
            content: {
              position: 'relative',
              inset: 'unset',
              top: 'unset',
              left: 'unset',
              right: 'unset',
              bottom: 'unset',
              transform: 'none',
              margin: 0,
              padding: 0,
              border: 'none',
            },
          }}
        >
          <button
            type="button"
            className="auth-modal-close"
            aria-label="Close"
            onClick={() => setIsAuthModalOpen(false)}
          >
            ×
          </button>
          <div className="auth-modal-hero">
            <div className="auth-modal-icon">
              <LuShieldCheck aria-hidden="true" />
            </div>
            <h2>Your family asset vault — start free</h2>
            <p>
              Sign in with Google to record assets, add nominees, and keep everything organized in one secure place.
            </p>
          </div>
          <div className="auth-modal-body">
            <ul className="auth-modal-benefits">
              <li><LuCheck aria-hidden="true" /> Add bank, PPF, EPF, stocks, insurance &amp; more</li>
              <li><LuCheck aria-hidden="true" /> Map nominees so your family knows where to begin</li>
              <li><LuCheck aria-hidden="true" /> Encrypted storage — your data stays yours</li>
            </ul>
            <div id={GOOGLE_MODAL_BUTTON_ID} className="auth-modal-google" />
            {!isGoogleButtonRendered && (
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <button type="button" className="auth-modal-fallback-btn" onClick={renderGoogleFallback}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.5 33.6 29.1 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.7 1.1 7.8 3l5.7-5.7C33.7 6.1 29.1 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c10.4 0 19-8.4 19-19 0-1.3-.1-2.2-.4-3.5z"/>
                    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.8 16.4 19 14 24 14c3 0 5.7 1.1 7.8 3l5.7-5.7C33.7 6.1 29.1 4 24 4c-7.5 0-13.9 4.3-17.7 10.7z"/>
                    <path fill="#4CAF50" d="M24 44c5 0 9.6-1.9 13-5l-6-4.9c-2 1.4-4.5 2.3-7 2.3-5 0-9.4-3.4-10.9-8.1l-6.6 5.1C8.3 39.6 15.6 44 24 44z"/>
                    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-1.3 3.6-5 6-9.3 6-5 0-9.4-3.4-10.9-8.1l-6.6 5.1C10.4 39.6 17.7 44 26 44c10.4 0 19-8.4 19-19 0-1.3-.1-2.2-.4-3.5z"/>
                  </svg>
                  Sign in with Google
                </button>
              </div>
            )}
            <p className="auth-modal-trust">Free to start · No credit card · Takes under a minute</p>
          </div>
        </Modal>
      </header>

      {!user ? (
        <>
          {isMobileMenuOpen ? (
            <button
              type="button"
              className="public-drawer-backdrop is-visible"
              aria-label="Close navigation menu"
              onClick={closeMobileMenu}
            />
          ) : null}

          <aside
            id="public-mobile-drawer"
            className={`public-drawer ${isMobileMenuOpen ? 'is-open' : ''}`}
            aria-label="Mobile navigation"
            aria-hidden={!isMobileMenuOpen}
          >
            <div className="public-drawer-inner">
              <p className="public-drawer-label">Navigation</p>
              <nav className="public-drawer-nav">
                <Link
                  to="/"
                  className={`public-drawer-link ${isHomeActive ? 'active' : ''}`}
                  onClick={handleHomeNav}
                >
                  <span className="public-drawer-icon" aria-hidden="true">
                    <LuHouse />
                  </span>
                  <span className="public-drawer-text">
                    <strong>Home</strong>
                    <small>Back to landing</small>
                  </span>
                  {isHomeActive ? <span className="public-drawer-active-dot" aria-hidden="true" /> : null}
                </Link>

                <Link
                  to="/#features"
                  className={`public-drawer-link ${isFeaturesActive ? 'active' : ''}`}
                  onClick={handleFeaturesNav}
                >
                  <span className="public-drawer-icon" aria-hidden="true">
                    <LuLayoutGrid />
                  </span>
                  <span className="public-drawer-text">
                    <strong>Features</strong>
                    <small>What KeepMyAsset offers</small>
                  </span>
                  {isFeaturesActive ? <span className="public-drawer-active-dot" aria-hidden="true" /> : null}
                </Link>

                <Link
                  to="/how-it-works"
                  className={`public-drawer-link ${isNavActive('/how-it-works') ? 'active' : ''}`}
                  onClick={closeMobileMenu}
                >
                  <span className="public-drawer-icon" aria-hidden="true">
                    <LuRoute />
                  </span>
                  <span className="public-drawer-text">
                    <strong>How it works</strong>
                    <small>Step-by-step guide</small>
                  </span>
                  {isNavActive('/how-it-works') ? <span className="public-drawer-active-dot" aria-hidden="true" /> : null}
                </Link>

                <Link
                  to="/security"
                  className={`public-drawer-link ${isNavActive('/security') ? 'active' : ''}`}
                  onClick={closeMobileMenu}
                >
                  <span className="public-drawer-icon" aria-hidden="true">
                    <LuShieldCheck />
                  </span>
                  <span className="public-drawer-text">
                    <strong>Security</strong>
                    <small>How we protect your data</small>
                  </span>
                  {isNavActive('/security') ? <span className="public-drawer-active-dot" aria-hidden="true" /> : null}
                </Link>

                <Link
                  to="/faq"
                  className={`public-drawer-link ${isNavActive('/faq') ? 'active' : ''}`}
                  onClick={closeMobileMenu}
                >
                  <span className="public-drawer-icon" aria-hidden="true">
                    <LuCircleHelp />
                  </span>
                  <span className="public-drawer-text">
                    <strong>FAQ</strong>
                    <small>Common questions answered</small>
                  </span>
                  {isNavActive('/faq') ? <span className="public-drawer-active-dot" aria-hidden="true" /> : null}
                </Link>

                <Link
                  to="/about"
                  className={`public-drawer-link ${isNavActive('/about') ? 'active' : ''}`}
                  onClick={closeMobileMenu}
                >
                  <span className="public-drawer-icon" aria-hidden="true">
                    <LuInfo />
                  </span>
                  <span className="public-drawer-text">
                    <strong>About</strong>
                    <small>Our mission &amp; story</small>
                  </span>
                  {isNavActive('/about') ? <span className="public-drawer-active-dot" aria-hidden="true" /> : null}
                </Link>

                <Link
                  to="/blog"
                  className={`public-drawer-link ${isNavActive('/blog') ? 'active' : ''}`}
                  onClick={closeMobileMenu}
                >
                  <span className="public-drawer-icon" aria-hidden="true">
                    <LuBookOpen />
                  </span>
                  <span className="public-drawer-text">
                    <strong>Blog</strong>
                    <small>Nominees, assets &amp; family guides</small>
                  </span>
                  {isNavActive('/blog') ? <span className="public-drawer-active-dot" aria-hidden="true" /> : null}
                </Link>

                <Link
                  to="/feedback"
                  className={`public-drawer-link ${isNavActive('/feedback') ? 'active' : ''}`}
                  onClick={closeMobileMenu}
                >
                  <span className="public-drawer-icon" aria-hidden="true">
                    <LuMessageSquare />
                  </span>
                  <span className="public-drawer-text">
                    <strong>Feedback</strong>
                    <small>Share ideas &amp; issues</small>
                  </span>
                  {isNavActive('/feedback') ? <span className="public-drawer-active-dot" aria-hidden="true" /> : null}
                </Link>

                <a
                  href="https://wa.me/918318959079"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="public-drawer-link"
                  onClick={closeMobileMenu}
                >
                  <span className="public-drawer-icon" aria-hidden="true">
                    <LuMessageCircle />
                  </span>
                  <span className="public-drawer-text">
                    <strong>Contact</strong>
                    <small>Chat with us on WhatsApp</small>
                  </span>
                </a>
              </nav>

              <div className="public-drawer-footer">
                <button
                  type="button"
                  className="public-drawer-signin"
                  onClick={() => { closeMobileMenu(); setIsAuthModalOpen(true); }}
                >
                  Login
                </button>
                <button
                  type="button"
                  className="public-drawer-cta"
                  onClick={() => { closeMobileMenu(); setIsAuthModalOpen(true); }}
                >
                  Secure Assets
                </button>
              </div>
            </div>
          </aside>
        </>
      ) : null}
    </>
  );
};

export default Header;
